// src/pages/projects/settings/ProjectFxSettingsPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import Modal from 'src/components/common/ui/Modal';
import { getProjectFxSettings, lockProjectFxSettings, updateProjectFxSettings } from 'src/features/projects/api';
import type { ProjectFxAnchorRow, ProjectFxSetting } from 'src/features/projects/types';

type RowUI = {
    fromCurrency: string;
    anchorRateText: string; // input text
    unit: number;
    note: string;

    // UX states
    isNew: boolean;
    isEditing: boolean; // editable 여부
    isConfirmed: boolean; // Confirm 되었는지
    removed: boolean;
};

const currencyOptionsTop = ['KRW', 'USD', 'EUR', 'JPY', 'CNY', 'GBP', 'CHF', 'HKD', 'SGD', 'AUD', 'CAD'];
const currencyOptionsMore = [
    'TWD',
    'THB',
    'VND',
    'INR',
    'IDR',
    'MYR',
    'PHP',
    'SAR',
    'AED',
    'QAR',
    'KWD',
    'NOK',
    'SEK',
    'DKK',
];
const currencyOptions = Array.from(new Set([...currencyOptionsTop, ...currencyOptionsMore])).sort((a, b) =>
    a.localeCompare(b),
);

function normIso3(s?: string) {
    return (s ?? '').trim().toUpperCase();
}
function isIso3(s: string) {
    return /^[A-Z]{3}$/.test(s);
}
function numOrNullFromText(t: string) {
    const x = Number(String(t).replace(/,/g, '').trim());
    return Number.isFinite(x) ? x : null;
}

export default function ProjectFxSettingsPage() {
    const { projectId } = useParams();
    const qc = useQueryClient();

    const fxQ = useQuery({
        queryKey: ['projectFx', projectId],
        enabled: !!projectId,
        queryFn: () => getProjectFxSettings(projectId as string),
    });

    const server: ProjectFxSetting | null = fxQ.data?.data ?? null;

    // ---- local UI state
    const [anchorCurrency, setAnchorCurrency] = useState('KRW');
    const [rows, setRows] = useState<RowUI[]>([]);
    const [lockOpen, setLockOpen] = useState(false);
    const [lockPhrase, setLockPhrase] = useState('');

    // 원본 스냅샷(변경 비교용)
    const originalRef = useRef<{
        anchorCurrency: string;
        byCur: Map<string, { anchorRateText: string; unit: number; note: string }>;
    } | null>(null);

    const locked = !!server?.lockedAt;

    // ---- 서버 값 로드 시 초기화
    useEffect(() => {
        if (!server) return;

        setAnchorCurrency(server.anchorCurrency || 'KRW');

        const uiRows: RowUI[] = server.rates.map((r) => ({
            fromCurrency: r.fromCurrency,
            anchorRateText: r.anchorRate, // string
            unit: r.unit ?? 1,
            note: r.note ?? '',
            isNew: false,
            isEditing: false,
            isConfirmed: true, // 서버에 존재 = 확정 상태로 간주
            removed: false,
        }));

        setRows(uiRows);

        const map = new Map<string, { anchorRateText: string; unit: number; note: string }>();
        for (const r of uiRows) {
            map.set(r.fromCurrency, { anchorRateText: r.anchorRateText, unit: r.unit, note: r.note });
        }
        originalRef.current = { anchorCurrency: server.anchorCurrency || 'KRW', byCur: map };
    }, [server?.projectId, server?.anchorCurrency, server?.rates]);

    // ---- mutations
    const mSave = useMutation({
        mutationFn: (payload: {
            anchorCurrency?: string;
            upserts?: Array<{ fromCurrency: string; anchorRate: number; unit?: number; note?: string | null }>;
            deletes?: Array<{ fromCurrency: string }>;
        }) => updateProjectFxSettings(projectId as string, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['projectFx', projectId] });
        },
    });

    const mLock = useMutation({
        mutationFn: () => lockProjectFxSettings(projectId as string),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['projectFx', projectId] });
        },
    });

    // ---- derived: dirty 계산(“Confirm된 변경만 저장” 컨셉)
    const { hasPendingSave, pendingUpserts, pendingDeletes, hasAnchorChange } = useMemo(() => {
        const orig = originalRef.current;
        const upserts: Array<{ fromCurrency: string; anchorRate: number; unit?: number; note?: string | null }> = [];
        const deletes: Array<{ fromCurrency: string }> = [];

        const anchorChanged = !!orig && normIso3(orig.anchorCurrency) !== normIso3(anchorCurrency);

        // deletes: removed true인 것 중, 원래 서버에 있던 것만 삭제 대상으로
        for (const r of rows) {
            const cur = normIso3(r.fromCurrency);
            if (!isIso3(cur)) continue;

            if (r.removed) {
                // 기존에 있던 것만 삭제
                if (orig?.byCur.has(cur)) deletes.push({ fromCurrency: cur });
                continue;
            }

            // Confirm된 것만 서버 반영 대상으로
            if (!r.isConfirmed) continue;

            const rateNum = numOrNullFromText(r.anchorRateText);
            if (rateNum == null || rateNum <= 0) continue;

            // 신규 또는 기존값 대비 변경된 경우만 upsert
            const o = orig?.byCur.get(cur);
            const changed =
                !o ||
                String(o.anchorRateText) !== String(r.anchorRateText) ||
                o.unit !== r.unit ||
                String(o.note ?? '') !== String(r.note ?? '');

            if (changed) {
                upserts.push({
                    fromCurrency: cur,
                    anchorRate: rateNum,
                    unit: r.unit,
                    note: (r.note ?? '').trim() ? r.note.trim() : null,
                });
            }
        }

        return {
            hasAnchorChange: anchorChanged,
            pendingUpserts: upserts,
            pendingDeletes: deletes,
            hasPendingSave: anchorChanged || upserts.length > 0 || deletes.length > 0,
        };
    }, [anchorCurrency, rows]);

    // ---- UI actions
    const addRow = () => {
        if (locked) return;
        setRows((prev) => [
            ...prev,
            {
                fromCurrency: 'USD',
                anchorRateText: '',
                unit: 1,
                note: '',
                isNew: true,
                isEditing: true,
                isConfirmed: false,
                removed: false,
            },
        ]);
    };

    const setRow = (idx: number, patch: Partial<RowUI>) => {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const removeRow = (idx: number) => {
        if (locked) return;
        setRow(idx, { removed: true });
    };

    const resetRow = (idx: number) => {
        const r = rows[idx];
        const cur = normIso3(r.fromCurrency);
        const orig = originalRef.current?.byCur.get(cur);

        if (!orig) {
            // 원래 없던 신규 행이면 제거로 처리
            setRow(idx, { removed: true });
            return;
        }

        setRow(idx, {
            anchorRateText: orig.anchorRateText,
            unit: orig.unit,
            note: orig.note,
            isEditing: false,
            isConfirmed: true,
            removed: false,
            isNew: false,
        });
    };

    const statusLabel = (r: RowUI) => {
        const orig = originalRef.current;
        const cur = normIso3(r.fromCurrency);
        if (!isIso3(cur)) return '—';

        if (r.removed) return '삭제 예정';

        const o = orig?.byCur.get(cur);
        if (!o) {
            return r.isConfirmed ? '추가됨' : '추가됨(미확정)';
        }

        const changed =
            String(o.anchorRateText) !== String(r.anchorRateText) ||
            o.unit !== r.unit ||
            String(o.note) !== String(r.note);

        if (!changed) return '—';

        const from = `${o.anchorRateText}${o.unit === 1 ? '' : ` /${o.unit}`}`;
        const to = `${r.anchorRateText}${r.unit === 1 ? '' : ` /${r.unit}`}`;
        return `수정됨 (${from} → ${to})`;
    };

    const onSave = async () => {
        if (!projectId) return;
        if (locked) return;

        await mSave.mutateAsync({
            ...(hasAnchorChange ? { anchorCurrency: normIso3(anchorCurrency) } : {}),
            ...(pendingUpserts.length ? { upserts: pendingUpserts } : {}),
            ...(pendingDeletes.length ? { deletes: pendingDeletes } : {}),
        });
    };

    const doLock = async () => {
        if (!projectId) return;
        await mLock.mutateAsync();
    };

    // ---- anchor options
    const anchorOptions = useMemo(() => currencyOptions, []);

    if (fxQ.isLoading) return <div className="p-4 text-xs text-gray-500">FX settings 불러오는 중…</div>;
    if (fxQ.isError) return <div className="p-4 text-xs text-red-600">FX settings 로드 실패</div>;
    if (!server) return <div className="p-4 text-xs text-gray-500">데이터 없음</div>;

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-gray-800">FX Settings</div>
                    <div className="text-xs text-gray-500 mt-1">
                        입력 기준 통화(Anchor)로 환율표를 관리합니다. (예: 1 USD = 1450 KRW)
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        Contract Currency:{' '}
                        <span className="font-medium text-gray-800">{server.contractCurrency ?? '—'}</span>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="text-xs rounded-lg border px-3 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-40"
                        onClick={addRow}
                        disabled={locked}
                    >
                        + Add
                    </button>

                    <button
                        type="button"
                        className="text-xs rounded-lg border px-3 py-1.5 bg-sky-600 text-white border-sky-600 hover:bg-sky-700 disabled:opacity-40"
                        onClick={onSave}
                        disabled={locked || !hasPendingSave || mSave.isPending}
                        title={
                            locked
                                ? '잠금 상태에서는 저장할 수 없습니다.'
                                : !hasPendingSave
                                ? '저장할 변경이 없습니다.'
                                : '저장'
                        }
                    >
                        {mSave.isPending ? 'Saving…' : 'Save'}
                    </button>

                    <button
                        type="button"
                        className="text-xs rounded-lg border px-3 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-40"
                        disabled={locked || mLock.isPending || hasPendingSave}
                        onClick={() => {
                            setLockPhrase('');
                            setLockOpen(true);
                        }}
                        title={
                            locked
                                ? '이미 잠금 상태입니다.'
                                : hasPendingSave
                                ? '먼저 Save로 변경을 저장한 뒤 Lock 하세요.'
                                : '잠금 이후에는 수정이 불가합니다'
                        }
                    >
                        Lock
                    </button>
                </div>
            </div>

            {/* Anchor Currency */}
            <div className="rounded-2xl border bg-white p-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-600">입력 기준 통화(Anchor)</div>
                    <div className="text-xs text-gray-500">
                        {locked ? (
                            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5">
                                🔒 Locked {server.lockedAt ? `(${new Date(server.lockedAt).toLocaleString()})` : ''}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5">
                                Editable
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-2 flex items-center gap-2">
                    <select
                        className="text-sm rounded-lg border px-2 py-1 disabled:bg-gray-50"
                        value={anchorCurrency}
                        onChange={(e) => setAnchorCurrency(normIso3(e.target.value))}
                        disabled={locked}
                    >
                        {anchorOptions.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>

                    <div className="text-xs text-gray-500">
                        입력 예: From=USD, Rate=1450 →{' '}
                        <span className="font-medium">1 USD = 1450 {anchorCurrency}</span>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-2xl border bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-[920px] w-full text-xs">
                        <thead className="bg-gray-50 border-b">
                            <tr className="text-gray-600">
                                <th className="text-left px-3 py-2 w-[140px]">From</th>
                                <th className="text-left px-3 py-2 w-[220px]">Rate</th>
                                <th className="text-left px-3 py-2 w-[120px]">Unit</th>
                                <th className="text-left px-3 py-2">Note</th>
                                <th className="text-left px-3 py-2 w-[220px]">Status</th>
                                <th className="text-right px-3 py-2 w-[240px]">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td className="px-3 py-4 text-gray-500" colSpan={6}>
                                        환율이 없습니다. Add로 추가하세요.
                                    </td>
                                </tr>
                            )}

                            {rows.map((r, idx) => {
                                if (r.removed) {
                                    return (
                                        <tr key={`${idx}-${r.fromCurrency}`} className="border-b bg-rose-50/40">
                                            <td className="px-3 py-2 font-medium">{r.fromCurrency}</td>
                                            <td className="px-3 py-2 text-gray-500" colSpan={3}>
                                                삭제 예정
                                            </td>
                                            <td className="px-3 py-2 text-rose-700">{statusLabel(r)}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    className="text-xs rounded-lg border px-2 py-1 bg-white hover:bg-gray-50"
                                                    disabled={locked}
                                                    onClick={() => setRow(idx, { removed: false })}
                                                >
                                                    Undo
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                }

                                const cur = normIso3(r.fromCurrency);
                                const rateSuffix = anchorCurrency;

                                return (
                                    <tr key={`${idx}-${r.fromCurrency}`} className="border-b">
                                        {/* From */}
                                        <td className="px-3 py-2">
                                            <select
                                                className="text-sm rounded-lg border px-2 py-1 disabled:bg-gray-50"
                                                value={cur}
                                                disabled={locked || !r.isEditing}
                                                onChange={(e) =>
                                                    setRow(idx, { fromCurrency: normIso3(e.target.value) })
                                                }
                                            >
                                                {currencyOptions.map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Rate */}
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    className="text-sm rounded-lg border px-2 py-1 w-full disabled:bg-gray-50"
                                                    value={r.anchorRateText}
                                                    disabled={locked || !r.isEditing}
                                                    onChange={(e) => setRow(idx, { anchorRateText: e.target.value })}
                                                    placeholder="e.g. 1450"
                                                />
                                                <span className="text-[11px] text-gray-500 whitespace-nowrap">
                                                    {rateSuffix}
                                                </span>
                                            </div>
                                            <div className="mt-1 text-[11px] text-gray-500">
                                                의미: <span className="font-medium">1 {cur}</span> ={' '}
                                                {r.anchorRateText || '—'} {anchorCurrency}
                                            </div>
                                        </td>

                                        {/* Unit */}
                                        <td className="px-3 py-2">
                                            <select
                                                className="text-sm rounded-lg border px-2 py-1 disabled:bg-gray-50"
                                                value={r.unit}
                                                disabled={locked || !r.isEditing}
                                                onChange={(e) => setRow(idx, { unit: Number(e.target.value) })}
                                            >
                                                <option value={1}>1</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <div className="mt-1 text-[11px] text-gray-500">
                                                JPY 등 100단위 입력 지원
                                            </div>
                                        </td>

                                        {/* Note */}
                                        <td className="px-3 py-2">
                                            <input
                                                className="text-sm rounded-lg border px-2 py-1 w-full disabled:bg-gray-50"
                                                value={r.note}
                                                disabled={locked || !r.isEditing}
                                                onChange={(e) => setRow(idx, { note: e.target.value })}
                                                placeholder="note..."
                                            />
                                        </td>

                                        {/* Status */}
                                        <td className="px-3 py-2">
                                            <span className="text-[11px] text-gray-700">{statusLabel(r)}</span>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="text-xs rounded-lg border px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-40"
                                                    disabled={locked}
                                                    onClick={() => setRow(idx, { isEditing: true })}
                                                >
                                                    Edit
                                                </button>

                                                <button
                                                    type="button"
                                                    className="text-xs rounded-lg border px-2 py-1 bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                                                    disabled={locked}
                                                    onClick={() => setRow(idx, { isEditing: false, isConfirmed: true })}
                                                    title="Confirm되면 저장 대상이 됩니다."
                                                >
                                                    Confirm
                                                </button>

                                                <button
                                                    type="button"
                                                    className="text-xs rounded-lg border px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-40"
                                                    disabled={locked}
                                                    onClick={() => resetRow(idx)}
                                                >
                                                    Reset
                                                </button>

                                                <button
                                                    type="button"
                                                    className="text-xs rounded-lg border px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-40"
                                                    disabled={locked}
                                                    onClick={() => removeRow(idx)}
                                                >
                                                    Remove
                                                </button>
                                            </div>

                                            {!r.isConfirmed && (
                                                <div className="mt-1 text-[11px] text-amber-600">
                                                    ※ 미확정(Confirm 필요)
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="px-3 py-2 text-[11px] text-gray-500 border-t bg-gray-50">
                    저장 정책: <span className="font-medium">Confirm된 변경만</span> 서버로 전송됩니다.
                </div>
            </div>

            {/* Strong Lock Modal */}
            <Modal
                open={lockOpen}
                onClose={() => setLockOpen(false)}
                title="⚠️ FX Settings Lock (되돌릴 수 없음)"
                size="lg"
                footer={
                    <div className="flex items-center justify-between gap-2">
                        <button
                            type="button"
                            className="text-xs rounded-lg border px-3 py-1.5 bg-white hover:bg-gray-50"
                            onClick={() => setLockOpen(false)}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="text-xs rounded-lg border px-3 py-1.5 bg-red-600 text-white border-red-600 hover:bg-red-700 disabled:opacity-40"
                            disabled={lockPhrase.trim() !== 'LOCK' || mLock.isPending}
                            onClick={async () => {
                                await doLock();
                                setLockOpen(false);
                            }}
                            title={lockPhrase.trim() !== 'LOCK' ? 'LOCK을 정확히 입력해야 합니다.' : ''}
                        >
                            {mLock.isPending ? 'Locking…' : 'Lock Now'}
                        </button>
                    </div>
                }
            >
                <div className="space-y-3 text-sm">
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                        <div className="font-semibold text-red-800">이 작업은 매우 위험합니다.</div>
                        <ul className="mt-2 text-xs text-red-700 space-y-1 list-disc pl-5">
                            <li>잠금 이후에는 Anchor Currency 및 환율을 수정할 수 없습니다.</li>
                            <li>계약/CO/Payment/요약 금액 계산이 이 환율 기준으로 고정됩니다.</li>
                            <li>변경이 필요하면 Service Admin 수동 조치가 필요합니다.</li>
                        </ul>
                    </div>

                    <div className="rounded-xl border bg-white p-3">
                        <div className="text-xs text-gray-500 mb-1">현재 잠금 대상</div>
                        <div className="text-sm text-gray-800">
                            Anchor Currency: <span className="font-semibold">{normIso3(anchorCurrency)}</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                            Rows: <span className="font-medium">{rows.filter((r) => !r.removed).length}</span>개
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500">
                            * 저장되지 않은 변경이 있으면 Lock 버튼이 비활성화됩니다.
                        </div>
                    </div>

                    <div className="rounded-xl border bg-white p-3">
                        <div className="text-xs text-gray-500 mb-2">
                            계속하려면 아래 입력창에 <span className="font-semibold">LOCK</span> 을 정확히 입력하세요.
                        </div>
                        <input
                            value={lockPhrase}
                            onChange={(e) => setLockPhrase(e.target.value)}
                            className="w-full rounded-lg border px-3 py-2 text-sm"
                            placeholder='Type "LOCK" to confirm'
                            autoFocus
                        />
                        <div className="mt-2 text-[11px] text-gray-500">(대소문자 구분, 정확히 LOCK)</div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
